// ============================================================
// 🤖 مدير المساعد الذكي — لوحة تحكم واحدة للبوت (ويب + تليجرام)
// المسار: src/components/pages/BotManager.jsx
//   تابات: الشخصية والقواعد | قاعدة المعرفة | المحادثات | الاشتراكات | تليجرام
//   كل القراءة/الكتابة عبر السيرفر (/api/admin/bot/*) بتوكن الموظف.
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiUrl } from '../../config/supabase';
import { KnowledgeManager } from './Dashboard';

const empToken = () => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } };

async function botApi(path, body = {}) {
  try {
    const res = await fetch(apiUrl(`/api/admin/bot/${path}`), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: empToken(), ...body }),
    });
    const r = await res.json().catch(() => ({}));
    if (res.ok && r.ok) return r;
    return { error: r.message || 'حصلت مشكلة' };
  } catch { return { error: 'فشل الاتصال بالسيرفر' }; }
}

const INP = { padding: '8px 10px', border: '1px solid #334155', borderRadius: '8px', fontSize: '13px', width: '100%', boxSizing: 'border-box', outline: 'none', background: '#1e293b', color: 'white', fontFamily: 'inherit' };
const LBL = { fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' };
const CARD = { background: '#1e293b', borderRadius: '12px', padding: '14px', border: '1px solid #334155', marginBottom: '12px' };
const BTN_P = { padding: '8px 16px', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' };
const BTN_G = { padding: '6px 10px', background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' };
const BTN_D = { padding: '5px 8px', background: '#7f1d1d', color: '#f87171', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' };

const STATE_LABEL = {
  open: ['مشترك', '#166534', '#4ade80'],
  trial: ['تجربة مجانية', '#1e3a5f', '#60a5fa'],
  login_required: ['بدون دخول', '#3f3f46', '#d4d4d8'],
  enroll_required: ['مش مشترك', '#78350f', '#fbbf24'],
  expired: ['منتهي', '#7f1d1d', '#f87171'],
  locked: ['مقفول', '#3f3f46', '#d4d4d8'],
};
const Badge = ({ state }) => {
  const [txt, bg, fg] = STATE_LABEL[state] || [state, '#334155', '#cbd5e1'];
  return <span style={{ background: bg, color: fg, padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>{txt}</span>;
};
const fmt = (d) => d ? new Date(d).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtD = (d) => d ? new Date(d).toLocaleDateString('ar-EG') : '—';

// ── تاب 1: الشخصية والقواعد ─────────────────────────────────
function SettingsTab({ notify }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { botApi('settings').then(r => setS(r.settings || {})); }, []);
  const set = (k) => (e) => setS(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    setSaving(true);
    const r = await botApi('settings-save', { settings: s });
    setSaving(false);
    if (r.error) notify(r.error, 'error'); else { setS(r.settings); notify('✅ اتحفظت — البوت هيشتغل بيها فورًا في الويب وتليجرام'); }
  };
  if (!s) return <div style={{ color: '#94a3b8', textAlign: 'center', padding: 30 }}>جاري التحميل...</div>;
  const TA = (k, rows = 4) => <textarea value={s[k] ?? ''} onChange={set(k)} rows={rows} style={{ ...INP, resize: 'vertical', lineHeight: 1.7 }} />;
  return (
    <div>
      <div style={{ ...CARD, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <label style={{ color: 'white', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={!!s.is_enabled} onChange={set('is_enabled')} /> المساعد شغال
        </label>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={LBL}>اسم البوت (بيظهر في الويدجت)</label>
          <input value={s.bot_name ?? ''} onChange={set('bot_name')} style={INP} />
        </div>
      </div>

      <div style={CARD}>
        <label style={LBL}>🧠 الشخصية — مين هو البوت وبيتكلم باسم مين</label>{TA('persona', 3)}
        <label style={{ ...LBL, marginTop: 10 }}>🗣️ الأسلوب والنبرة</label>{TA('tone', 3)}
        <label style={{ ...LBL, marginTop: 10 }}>📏 القواعد (سطر لكل قاعدة)</label>{TA('rules', 7)}
        <label style={{ ...LBL, marginTop: 10 }}>👋 رسالة الترحيب (أول ما يفتح الشات)</label>{TA('welcome_message', 2)}
      </div>

      <div style={CARD}>
        <div style={{ color: 'white', fontWeight: 'bold', fontSize: 13, marginBottom: 10 }}>🔒 الحارس — مين يقدر يستخدم البوت</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
          <div><label style={LBL}>عدد الرسائل المجانية للزائر</label><input type="number" min="0" value={s.free_trial_count ?? 1} onChange={set('free_trial_count')} style={INP} /></div>
          <div><label style={LBL}>شهور المجانية بعد الاشتراك في كورس</label><input type="number" min="0" value={s.free_access_months ?? 12} onChange={set('free_access_months')} style={INP} /></div>
          <div><label style={LBL}>سعر التجديد الشهري (جنيه)</label><input type="number" value={s.renew_price_monthly ?? ''} onChange={set('renew_price_monthly')} style={INP} placeholder="اختياري" /></div>
          <div><label style={LBL}>سعر التجديد السنوي (جنيه)</label><input type="number" value={s.renew_price_yearly ?? ''} onChange={set('renew_price_yearly')} style={INP} placeholder="اختياري" /></div>
          <div><label style={LBL}>أقصى طول للرد (tokens)</label><input type="number" min="200" max="2000" value={s.max_reply_tokens ?? 700} onChange={set('max_reply_tokens')} style={INP} /></div>
        </div>
        <label style={LBL}>🎉 رسالة التفعيل (بعد ربط تليجرام / أول ما المشترك يفتح الشات) — استخدم {'{name}'} و {'{days}'}</label>{TA('activation_message', 4)}
        <label style={{ ...LBL, marginTop: 10 }}>رسالة بعد استهلاك التجربة المجانية</label>{TA('trial_used_message', 2)}
        <label style={{ ...LBL, marginTop: 10 }}>رسالة "لازم تسجل دخول"</label>{TA('login_required_message', 2)}
        <label style={{ ...LBL, marginTop: 10 }}>رسالة "مسجل بس مش مشترك في كورس"</label>{TA('enroll_required_message', 2)}
        <label style={{ ...LBL, marginTop: 10 }}>رسالة "انتهت السنة المجانية"</label>{TA('expired_message', 2)}
        <label style={{ ...LBL, marginTop: 10 }}>ملاحظة التجديد (تظهر مع الأسعار)</label>{TA('renew_note', 2)}
        <label style={{ ...LBL, marginTop: 10 }}>تعليمات البوت في وضع "مقفول" (بيبيع بس)</label>{TA('locked_mode_hint', 4)}
      </div>

      <VoiceSection s={s} set={set} setS={setS} notify={notify} />

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start' }}>
        <button onClick={save} disabled={saving} style={BTN_P}>{saving ? '...' : '💾 حفظ الإعدادات'}</button>
      </div>
    </div>
  );
}

// ── 🔊 قسم الصوت: المتحدث + سلسلة المزوّدين (أساسي → بديل → بديل) + مفتاح لكل مزوّد + اختبار ──
const PROVIDER_META = {
  gemini:     { label: '✨ Google Gemini', hint: 'مفتاح من aistudio.google.com/apikey — بيفهم اللهجة المصرية كويس', voices: { female: ['Kore', 'Aoede', 'Leda', 'Zephyr', 'Achernar', 'Sulafat'], male: ['Puck', 'Charon', 'Fenrir', 'Orus', 'Enceladus', 'Iapetus'] } },
  elevenlabs: { label: '🎙️ ElevenLabs', hint: 'مفتاح من elevenlabs.io → Profile → API Keys — أعلى جودة', voices: { female: ['21m00Tcm4TlvDq8ikWAM (Rachel)', 'EXAVITQu4vr4xnSDxMaL (Sarah)'], male: ['pNInz6obpgDQGcFmaJgB (Adam)', 'TxGEqnHWrfWFTfGW9XjX (Josh)'] } },
  openai:     { label: '🤖 OpenAI', hint: 'مفتاح من platform.openai.com/api-keys (بيبدأ sk-)', voices: { female: ['nova', 'shimmer', 'coral', 'sage', 'alloy'], male: ['onyx', 'echo', 'ash', 'fable', 'ballad'] } },
  browser:    { label: '🌐 صوت المتصفح', hint: 'مجاني من جهاز العميل — جودة عادية والعربي مش متوفر على كل الأجهزة', voices: { female: [], male: [] } },
};
const TTS_ALL = ['gemini', 'elevenlabs', 'openai', 'browser'];
const STT_ALL = ['gemini', 'openai', 'browser'];
const parseOrder = (raw, allowed) => { const a = String(raw || '').split(',').map(x => x.trim()).filter(x => allowed.includes(x)); return a.length ? a : ['browser']; };

function OrderPicker({ value, allowed, onChange, label }) {
  const order = parseOrder(value, allowed);
  const rest = allowed.filter(p => !order.includes(p));
  const move = (i, d) => { const a = [...order]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; onChange(a.join(',')); };
  const remove = (i) => { const a = order.filter((_, k) => k !== i); onChange((a.length ? a : ['browser']).join(',')); };
  const add = (p) => onChange([...order, p].join(','));
  return (
    <div>
      <label style={LBL}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {order.map((p, i) => (
          <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '5px 8px' }}>
            <span style={{ color: i === 0 ? '#4ade80' : '#94a3b8', fontSize: 10, fontWeight: 'bold', minWidth: 46 }}>{i === 0 ? 'الأساسي' : `بديل ${i}`}</span>
            <span style={{ color: 'white', fontSize: 12, flex: 1 }}>{PROVIDER_META[p]?.label || p}</span>
            <button onClick={() => move(i, -1)} disabled={i === 0} style={{ ...BTN_G, padding: '2px 6px', opacity: i === 0 ? 0.3 : 1 }}>▲</button>
            <button onClick={() => move(i, 1)} disabled={i === order.length - 1} style={{ ...BTN_G, padding: '2px 6px', opacity: i === order.length - 1 ? 0.3 : 1 }}>▼</button>
            <button onClick={() => remove(i)} style={{ ...BTN_D, padding: '2px 6px' }}>✕</button>
          </div>
        ))}
        {rest.length > 0 && (
          <select value="" onChange={e => e.target.value && add(e.target.value)} style={{ ...INP, padding: '5px 8px', fontSize: 12 }}>
            <option value="">+ ضيف بديل…</option>
            {rest.map(p => <option key={p} value={p}>{PROVIDER_META[p]?.label}</option>)}
          </select>
        )}
      </div>
      <div style={{ color: '#64748b', fontSize: 10, marginTop: 4 }}>لو الأساسي فشل (مفتاح / رصيد / خدمة واقعة) البوت بيجرّب اللي بعده تلقائي.</div>
    </div>
  );
}

function VoiceSection({ s, set, setS, notify }) {
  const [testing, setTesting] = useState('');
  const [testText, setTestText] = useState('أهلاً بيك في أكاديمية الجوهرة! أنا المساعد الذكي، تحت أمرك في أي حاجة.');
  const gender = s.tts_gender || 'female';
  const ttsOrder = parseOrder(s.tts_order, TTS_ALL);
  const sttOrder = parseOrder(s.stt_order, STT_ALL);
  const providersInUse = [...new Set([...ttsOrder, ...sttOrder])].filter(p => p !== 'browser');

  const test = async (provider) => {
    setTesting(provider || 'chain');
    try {
      const settings = {};
      ['tts_gender', 'tts_style', 'tts_order', 'tts_key_gemini', 'tts_key_elevenlabs', 'tts_key_openai', 'tts_voice_gemini', 'tts_voice_elevenlabs', 'tts_voice_openai'].forEach(k => { settings[k] = s[k]; });
      const res = await fetch(apiUrl('/api/admin/bot/tts-test'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), text: testText, provider, settings }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); notify(j.message || j.error || 'فشل الاختبار', 'error'); }
      else { const used = res.headers.get('X-TTS-Provider'); const blob = await res.blob(); new Audio(URL.createObjectURL(blob)).play(); notify(`🔊 بيتشغّل من ${PROVIDER_META[used]?.label || used}`); }
    } catch { notify('فشل الاتصال بالسيرفر', 'error'); }
    setTesting('');
  };

  return (
    <div style={CARD}>
      <div style={{ color: 'white', fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>🔊 صوت المساعد (الويب + تليجرام)</div>
      <p style={{ color: '#94a3b8', fontSize: 11, marginTop: 0 }}>الإعدادات دي بتتطبق على الاتنين: رد الويدجت الصوتي، وفويسات تليجرام.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
        <div>
          <label style={LBL}>المتحدث</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['female', '👩 ست'], ['male', '👨 راجل']].map(([v, l]) => (
              <button key={v} onClick={() => setS(p => ({ ...p, tts_gender: v, tts_voice_gemini: '', tts_voice_elevenlabs: '', tts_voice_openai: '' }))}
                style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid #334155', cursor: 'pointer', fontSize: 12, fontWeight: 'bold', background: gender === v ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#0f172a', color: gender === v ? 'white' : '#94a3b8' }}>{l}</button>
            ))}
          </div>
          <label style={{ ...LBL, marginTop: 10 }}>توجيه الأداء (إزاي يتكلم)</label>
          <input value={s.tts_style || ''} onChange={set('tts_style')} style={INP} placeholder="اقري الكلام بلهجة مصرية عامية ودودة…" />
        </div>
        <OrderPicker label="مصدر الصوت (TTS) — بالترتيب" value={s.tts_order} allowed={TTS_ALL} onChange={v => setS(p => ({ ...p, tts_order: v }))} />
        <OrderPicker label="تفريغ صوت العميل (STT) — بالترتيب" value={s.stt_order} allowed={STT_ALL} onChange={v => setS(p => ({ ...p, stt_order: v }))} />
      </div>

      {/* مفتاح + صوت لكل مزوّد مستخدم */}
      {providersInUse.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #334155' }}>
          <div style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>🔑 مفاتيح وأصوات المزوّدين</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
            {providersInUse.map(p => {
              const meta = PROVIDER_META[p]; const keyK = `tts_key_${p}`; const voiceK = `tts_voice_${p}`;
              const list = meta.voices[gender] || []; const isTts = ttsOrder.includes(p);
              return (
                <div key={p} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 10 }}>
                  <div style={{ color: 'white', fontSize: 12, fontWeight: 'bold', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{meta.label}</span>
                    {s[`${keyK}_set`] ? <span style={{ color: '#4ade80', fontSize: 10 }}>✓ المفتاح محفوظ</span> : <span style={{ color: '#fbbf24', fontSize: 10 }}>مفيش مفتاح</span>}
                  </div>
                  <input type="password" value={s[keyK] || ''} onChange={set(keyK)} autoComplete="off"
                    placeholder={s[`${keyK}_set`] ? '•••••••• سيبه فاضي عشان يفضل القديم' : 'الصق API Key'} style={INP} />
                  <div style={{ color: '#64748b', fontSize: 10, marginTop: 3 }}>{meta.hint}</div>
                  {isTts && (
                    <div style={{ marginTop: 8 }}>
                      <input list={`vl-${p}`} value={s[voiceK] || ''} onChange={set(voiceK)} placeholder={`الصوت — افتراضي: ${(list[0] || '').split(' ')[0]}`} style={INP} />
                      <datalist id={`vl-${p}`}>{list.map(v => <option key={v} value={v.split(' ')[0]}>{v}</option>)}</datalist>
                    </div>
                  )}
                  {isTts && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button onClick={() => test(p)} disabled={!!testing} style={{ ...BTN_G, flex: 1 }}>{testing === p ? '...' : `▶ جرّب ${meta.label.split(' ').slice(1).join(' ')}`}</button>
                      {s[`${keyK}_set`] && <button onClick={() => { if (window.confirm('مسح المفتاح؟')) setS(x => ({ ...x, [keyK]: '__clear__' })); }} style={BTN_D}>مسح</button>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {ttsOrder[0] !== 'browser' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={testText} onChange={e => setTestText(e.target.value)} style={{ ...INP, flex: 1, minWidth: 200 }} />
          <button onClick={() => test()} disabled={!!testing} style={BTN_P}>{testing === 'chain' ? '...' : '▶ جرّب السلسلة كلها'}</button>
        </div>
      )}
      <div style={{ color: '#64748b', fontSize: 10, marginTop: 8 }}>💡 مفتاح Gemini واحد بيكفي للصوت والتفريغ مع بعض. الأزرار بتجرّب من غير حفظ — متنساش 💾 حفظ الإعدادات تحت.</div>
    </div>
  );
}

// ── تاب 3: المحادثات ────────────────────────────────────────
function ConversationsTab({ initial = {} }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ channel: initial.channel || '', state: initial.state || '', search: '', locked: !!initial.locked, today: !!initial.today, week: !!initial.week });
  useEffect(() => { setF({ channel: initial.channel || '', state: initial.state || '', search: '', locked: !!initial.locked, today: !!initial.today, week: !!initial.week }); }, [initial]); // eslint-disable-line
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    const since = f.today ? new Date(new Date().setHours(0, 0, 0, 0)).toISOString() : f.week ? new Date(Date.now() - 7 * 864e5).toISOString() : undefined;
    const r = await botApi('conversations', { channel: f.channel || undefined, state: f.state || undefined, search: f.search || undefined, locked: f.locked || undefined, since, limit: 200 });
    setRows(r.rows || []); setLoading(false);
  }, [f]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <select value={f.channel} onChange={e => setF(p => ({ ...p, channel: e.target.value }))} style={{ ...INP, width: 130 }}>
          <option value="">كل القنوات</option><option value="web">الويب</option><option value="telegram">تليجرام</option>
        </select>
        <select value={f.state} onChange={e => setF(p => ({ ...p, state: e.target.value }))} style={{ ...INP, width: 150 }}>
          <option value="">كل الحالات</option>
          {Object.keys(STATE_LABEL).filter(k => k !== 'locked').map(k => <option key={k} value={k}>{STATE_LABEL[k][0]}</option>)}
        </select>
        <input value={f.search} onChange={e => setF(p => ({ ...p, search: e.target.value }))} placeholder="بحث في الأسئلة..." style={{ ...INP, flex: 1, minWidth: 160 }} />
        <button onClick={load} style={BTN_G}>🔄</button>
        {(f.today || f.week || f.locked) && (
          <button onClick={() => setF(p => ({ ...p, today: false, week: false, locked: false }))} style={{ ...BTN_G, color: '#fbbf24' }}>
            {f.today ? 'النهارده' : f.week ? 'آخر 7 أيام' : ''}{f.locked ? ' · مقفولين' : ''} ✕
          </button>
        )}
      </div>
      {loading ? <div style={{ color: '#94a3b8', textAlign: 'center', padding: 30 }}>جاري التحميل...</div> :
        rows.length === 0 ? <div style={{ color: '#64748b', textAlign: 'center', padding: 30, border: '1px dashed #334155', borderRadius: 12 }}>مفيش محادثات لسه</div> :
        <GroupedConversations rows={rows} />}
    </div>
  );
}

// ── المحادثات متجمّعة بالشخص (الأحدث فوق) — تدوس على الشخص يفتح كل رسايله من الأحدث للأقدم ──
function GroupedConversations({ rows }) {
  const [open, setOpen] = useState(null);
  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.customer_id ? `c:${r.customer_id}` : r.telegram_id ? `t:${r.telegram_id}` : `v:${r.visitor_key || 'anon'}`;
      if (!map.has(key)) map.set(key, { key, name: r.customers?.name || r.customers?.username || (r.telegram_id ? `tg:${r.telegram_id}` : 'زائر'), username: r.customers?.username, channels: new Set(), states: new Set(), last: r.created_at, msgs: [] });
      const g = map.get(key);
      g.channels.add(r.channel); g.states.add(r.access_state); g.msgs.push(r);
      if (new Date(r.created_at) > new Date(g.last)) g.last = r.created_at;
    });
    return [...map.values()].sort((a, b) => new Date(b.last) - new Date(a.last));
  }, [rows]);
  const lastState = (g) => g.msgs[0]?.access_state;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
      {groups.map(g => (
        <div key={g.key} style={{ background: '#1e293b', borderRadius: 10, border: '1px solid #334155', overflow: 'hidden' }}>
          <div onClick={() => setOpen(open === g.key ? null : g.key)} style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: 'white', display: 'grid', placeItems: 'center', fontWeight: 'bold', fontSize: 13 }}>{(g.name || '?').charAt(0).toUpperCase()}</span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ color: 'white', fontWeight: 'bold', fontSize: 13 }}>{g.name}{g.username ? <span style={{ color: '#94a3b8', fontWeight: 'normal', fontSize: 11 }}> @{g.username}</span> : null}</span>
              <span style={{ color: '#94a3b8', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 420 }}>{g.msgs[0]?.user_message}</span>
            </div>
            <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              {[...g.channels].map(c => <span key={c} title={c} style={{ fontSize: 12 }}>{c === 'telegram' ? '📱' : '💻'}</span>)}
              <Badge state={lastState(g)} />
              <span style={{ background: '#0f172a', color: '#a5b4fc', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 'bold' }}>{g.msgs.length} رسالة</span>
              <span style={{ color: '#64748b', fontSize: 11 }}>{fmt(g.last)}</span>
              <span style={{ color: '#94a3b8', fontSize: 11 }}>{open === g.key ? '▲' : '▼'}</span>
            </span>
          </div>
          {open === g.key && (
            <div style={{ borderTop: '1px solid #334155', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6, background: '#0f172a' }}>
              {g.msgs.map(r => (
                <div key={r.id} style={{ background: '#1e293b', borderRadius: 8, padding: '8px 10px', border: '1px solid #334155' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11 }}>{r.channel === 'telegram' ? '📱' : '💻'}</span>
                    <Badge state={r.access_state} />
                    <span style={{ color: '#64748b', fontSize: 11, marginInlineStart: 'auto' }}>{fmt(r.created_at)}</span>
                  </div>
                  <div style={{ color: '#e2e8f0', fontSize: 12, whiteSpace: 'pre-wrap' }}>👤 {r.user_message}</div>
                  <div style={{ color: '#a5b4fc', fontSize: 12, marginTop: 6, whiteSpace: 'pre-wrap', borderTop: '1px solid #334155', paddingTop: 6 }}>🤖 {r.bot_reply}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── تاب 4: اشتراكات المساعد — نظرة عامة (نشط/يقرب ينتهي/منتهي) + أكواد التفعيل + اشتراك يدوي ──
function SubscriptionsTab({ notify, customers = [] }) {
  const [view, setView] = useState('overview'); // overview | codes | manual
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[['overview', '📊 نظرة عامة'], ['codes', '🎟️ أكواد التفعيل'], ['manual', '✍️ اشتراك يدوي']].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} style={{ ...BTN_G, background: view === k ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#0f172a', color: view === k ? 'white' : '#94a3b8', fontWeight: 'bold' }}>{l}</button>
        ))}
      </div>
      {view === 'overview' && <SubOverview notify={notify} />}
      {view === 'codes' && <CodesPanel notify={notify} customers={customers} />}
      {view === 'manual' && <ManualSubs notify={notify} customers={customers} />}
    </div>
  );
}

const BUCKET = { active: ['✅ نشط', '#166534', '#4ade80'], soon: ['⏳ يقرب ينتهي (≤30 يوم)', '#78350f', '#fbbf24'], expired: ['❌ انتهى ومجددش', '#7f1d1d', '#f87171'] };
function SubOverview({ notify }) {
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState('soon');
  const load = async () => { const r = await botApi('subscription-overview'); if (r.error) notify(r.error, 'error'); else setData(r); };
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!data) return <div style={{ color: '#94a3b8', textAlign: 'center', padding: 30 }}>جاري التحميل...</div>;
  const rows = (data.rows || []).filter(r => r.bucket === bucket);
  return (
    <div>
      <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 0 }}>بيجمع المشتركين مجانًا (سنة الكورس) والمدفوع. التنبيهات بتتبعت تلقائي للعميل على تليجرام قبل 30 يوم / 7 أيام / يوم واحد / يوم الانتهاء، وتقرير يومي ليك على تليجرام.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {Object.keys(BUCKET).map(k => (
          <button key={k} onClick={() => setBucket(k)} style={{ flex: 1, minWidth: 140, background: bucket === k ? BUCKET[k][1] : '#1e293b', border: `1px solid ${bucket === k ? BUCKET[k][2] : '#334155'}`, borderRadius: 12, padding: '10px 12px', cursor: 'pointer', textAlign: 'center' }}>
            <div style={{ color: 'white', fontWeight: 'bold', fontSize: 20 }}>{data.counts?.[k] || 0}</div>
            <div style={{ color: bucket === k ? BUCKET[k][2] : '#94a3b8', fontSize: 11 }}>{BUCKET[k][0]}</div>
          </button>
        ))}
        <button onClick={load} style={BTN_G}>🔄</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 460, overflowY: 'auto' }}>
        {rows.length === 0 && <div style={{ color: '#64748b', textAlign: 'center', padding: 24, border: '1px dashed #334155', borderRadius: 12 }}>مفيش حد في القائمة دي</div>}
        {rows.map(r => (
          <div key={r.customer_id} style={{ background: '#1e293b', borderRadius: 10, padding: '10px 12px', border: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ color: 'white', fontWeight: 'bold', fontSize: 13 }}>{r.name}</span>
            <span style={{ color: '#94a3b8', fontSize: 11 }}>@{r.username}</span>
            {r.phone && <a href={`https://wa.me/${String(r.phone).replace(/\D/g, '').replace(/^0/, '20')}`} target="_blank" rel="noreferrer" style={{ color: '#4ade80', fontSize: 11, textDecoration: 'none' }}>💬 {r.phone}</a>}
            {r.telegram && <span style={{ color: '#60a5fa', fontSize: 11 }}>📱 {r.telegram.telegram_username ? `@${r.telegram.telegram_username}` : 'مربوط'}</span>}
            <span style={{ color: '#a5b4fc', fontSize: 11 }}>{r.source === 'free' ? 'سنة الكورس' : r.source === 'paid_monthly' ? 'شهري' : r.source === 'paid_yearly' ? 'سنوي' : 'يدوي'}</span>
            <span style={{ marginInlineStart: 'auto', color: r.days_left < 0 ? '#f87171' : r.days_left <= 30 ? '#fbbf24' : '#4ade80', fontSize: 12, fontWeight: 'bold' }}>
              {r.days_left < 0 ? `انتهى من ${Math.abs(r.days_left)} يوم` : `باقي ${r.days_left} يوم`} · {fmtD(r.access_until)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CodesPanel({ notify, customers = [] }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('unused');
  const [gen, setGen] = useState({ plan: 'yearly', months: 12, count: 5, price: '', note: '' });
  const [made, setMade] = useState([]);
  const [busy, setBusy] = useState(false);
  const [redeemFor, setRedeemFor] = useState({ customer_id: '', code: '' });
  const [q, setQ] = useState('');
  const load = async () => { const r = await botApi('codes', { status: filter || undefined }); setRows(r.rows || []); };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  const generate = async () => {
    setBusy(true);
    const r = await botApi('codes-generate', { plan: gen.plan, months: gen.months, count: gen.count, price: gen.price, note: gen.note });
    setBusy(false);
    if (r.error) { notify(r.error, 'error'); return; }
    setMade(r.rows || []); notify(`✅ اتولّد ${(r.rows || []).length} كود`); load();
  };
  const copyAll = () => { navigator.clipboard?.writeText(made.map(c => c.code).join('\n')); notify('اتنسخوا 📋'); };
  const voidCode = async (code) => { if (!window.confirm(`إلغاء الكود ${code}؟`)) return; const r = await botApi('codes-void', { code }); if (r.error) notify(r.error, 'error'); else load(); };
  const redeem = async () => {
    if (!redeemFor.customer_id || !redeemFor.code) { notify('اختار العميل واكتب الكود', 'error'); return; }
    const r = await botApi('redeem-for', redeemFor);
    if (r.error) notify(r.message || r.error, 'error'); else { notify(`✅ اتفعّل حتى ${fmtD(r.ends_at)} — والعميل وصلته رسالة التفعيل`); setRedeemFor({ customer_id: '', code: '' }); load(); }
  };
  const filtered = customers.filter(c => !q || `${c.name} ${c.username}`.toLowerCase().includes(q.toLowerCase())).slice(0, 50);
  const planTxt = (c) => c.plan === 'monthly' ? 'شهري' : c.plan === 'yearly' ? 'سنوي' : `${c.months} شهر`;
  return (
    <div>
      <div style={CARD}>
        <div style={{ color: 'white', fontWeight: 'bold', fontSize: 13, marginBottom: 8 }}>➕ توليد أكواد</div>
        <p style={{ color: '#94a3b8', fontSize: 11, marginTop: 0 }}>الأسعار الافتراضية من إعدادات الحارس (الشهري/السنوي). بعد ما العميل يحوّل ويبعت الإيصال، ابعتله كود — يفعّله من صفحة الاشتراك أو يبعته للبوت (ويب/تليجرام) وتوصله رسالة التفعيل بالمدة تلقائي.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
          <div><label style={LBL}>الباقة</label>
            <select value={gen.plan} onChange={e => setGen(p => ({ ...p, plan: e.target.value, months: e.target.value === 'monthly' ? 1 : e.target.value === 'yearly' ? 12 : p.months }))} style={INP}>
              <option value="monthly">شهري (1 شهر)</option><option value="yearly">سنوي (12 شهر)</option><option value="custom">مخصص</option>
            </select></div>
          {gen.plan === 'custom' && <div><label style={LBL}>عدد الشهور</label><input type="number" min="1" value={gen.months} onChange={e => setGen(p => ({ ...p, months: e.target.value }))} style={INP} /></div>}
          <div><label style={LBL}>عدد الأكواد</label><input type="number" min="1" max="100" value={gen.count} onChange={e => setGen(p => ({ ...p, count: e.target.value }))} style={INP} /></div>
          <div><label style={LBL}>السعر (اختياري)</label><input type="number" value={gen.price} onChange={e => setGen(p => ({ ...p, price: e.target.value }))} placeholder="افتراضي من الإعدادات" style={INP} /></div>
          <div><label style={LBL}>ملاحظة</label><input value={gen.note} onChange={e => setGen(p => ({ ...p, note: e.target.value }))} placeholder="مثلًا: دفعة أغسطس" style={INP} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={generate} disabled={busy} style={BTN_P}>{busy ? '...' : '🎟️ ولّد الأكواد'}</button>
          {made.length > 0 && <button onClick={copyAll} style={BTN_G}>📋 انسخ الـ {made.length} كود</button>}
        </div>
        {made.length > 0 && (
          <div style={{ marginTop: 10, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 10, fontFamily: 'monospace', color: '#4ade80', fontSize: 13, direction: 'ltr', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 4 }}>
            {made.map(c => <span key={c.code}>{c.code}</span>)}
          </div>
        )}
      </div>

      <div style={CARD}>
        <div style={{ color: 'white', fontWeight: 'bold', fontSize: 13, marginBottom: 8 }}>⚡ تفعيل كود لعميل من هنا (لو مش عارف يفعّله بنفسه)</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 200 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="ابحث بالاسم..." style={{ ...INP, marginBottom: 6 }} />
            <select value={redeemFor.customer_id} onChange={e => setRedeemFor(p => ({ ...p, customer_id: e.target.value }))} style={INP}>
              <option value="">— اختار العميل —</option>
              {filtered.map(c => <option key={c.id} value={c.id}>{c.name} (@{c.username})</option>)}
            </select>
          </div>
          <input value={redeemFor.code} onChange={e => setRedeemFor(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="GA-XXXX-XXXX" dir="ltr" style={{ ...INP, flex: 1, minWidth: 160, fontFamily: 'monospace', alignSelf: 'end' }} />
          <button onClick={redeem} style={{ ...BTN_P, alignSelf: 'end' }}>✅ فعّل</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
        {[['unused', 'غير مستخدم'], ['used', 'مستخدم'], ['void', 'ملغي'], ['', 'الكل']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ ...BTN_G, background: filter === v ? '#334155' : '#0f172a', color: filter === v ? 'white' : '#94a3b8' }}>{l}</button>
        ))}
        <span style={{ color: '#64748b', fontSize: 11, marginInlineStart: 'auto' }}>{rows.length} كود</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 360, overflowY: 'auto' }}>
        {rows.map(c => (
          <div key={c.code} style={{ background: '#1e293b', borderRadius: 8, padding: '7px 10px', border: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
            <span style={{ fontFamily: 'monospace', color: c.status === 'unused' ? '#4ade80' : c.status === 'used' ? '#94a3b8' : '#f87171', fontWeight: 'bold', direction: 'ltr' }}>{c.code}</span>
            <span style={{ color: '#a5b4fc' }}>{planTxt(c)}{c.price ? ` — ${c.price} ج` : ''}</span>
            {c.note && <span style={{ color: '#64748b' }}>{c.note}</span>}
            {c.status === 'used' && <span style={{ color: '#94a3b8' }}>← {c.customers?.name || c.used_by} · {fmt(c.used_at)}</span>}
            <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ color: '#64748b', fontSize: 11 }}>{fmtD(c.created_at)}</span>
              {c.status === 'unused' && <><button onClick={() => { navigator.clipboard?.writeText(c.code); notify('اتنسخ'); }} style={BTN_G}>📋</button><button onClick={() => voidCode(c.code)} style={BTN_D}>إلغاء</button></>}
            </span>
          </div>
        ))}
        {rows.length === 0 && <div style={{ color: '#64748b', textAlign: 'center', padding: 20, border: '1px dashed #334155', borderRadius: 12 }}>مفيش أكواد</div>}
      </div>
    </div>
  );
}

function ManualSubs({ notify, customers = [] }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ id: null, customer_id: '', ends_at: '', source: 'manual', amount: '', note: '' });
  const [q, setQ] = useState('');
  const load = async () => { const r = await botApi('subscriptions'); setRows(r.rows || []); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.customer_id || !form.ends_at) { notify('اختار العميل وحدد تاريخ النهاية', 'error'); return; }
    const r = await botApi('subscription-save', { row: { ...form, ends_at: new Date(form.ends_at).toISOString() } });
    if (r.error) { notify(r.error, 'error'); return; }
    notify('✅ اتحفظ الاشتراك'); setShowForm(false); setForm({ id: null, customer_id: '', ends_at: '', source: 'manual', amount: '', note: '' }); load();
  };
  const del = async (id) => {
    if (!window.confirm('حذف الاشتراك ده؟')) return;
    const r = await botApi('subscription-delete', { id });
    if (r.error) notify(r.error, 'error'); else load();
  };
  const plusMonths = (m) => { const d = new Date(); d.setMonth(d.getMonth() + m); setForm(p => ({ ...p, ends_at: d.toISOString().slice(0, 10) })); };
  const filtered = customers.filter(c => !q || `${c.name} ${c.username}`.toLowerCase().includes(q.toLowerCase())).slice(0, 50);
  return (
    <div>
      <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 0 }}>المشترك في أي كورس بياخد المساعد ببلاش لمدة السنة المحددة تلقائيًا. هنا بتضيف اشتراك يدوي/مدفوع لما السنة تخلص.</p>
      <button onClick={() => setShowForm(v => !v)} style={{ ...BTN_P, marginBottom: 12 }}>+ إضافة اشتراك</button>
      {showForm && (
        <div style={CARD}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={LBL}>العميل</label>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="ابحث بالاسم أو اليوزر..." style={{ ...INP, marginBottom: 6 }} />
              <select value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))} style={INP}>
                <option value="">— اختار —</option>
                {filtered.map(c => <option key={c.id} value={c.id}>{c.name} (@{c.username})</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>ينتهي في</label>
              <input type="date" value={form.ends_at} onChange={e => setForm(p => ({ ...p, ends_at: e.target.value }))} style={INP} />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button onClick={() => plusMonths(1)} style={BTN_G}>+ شهر</button>
                <button onClick={() => plusMonths(12)} style={BTN_G}>+ سنة</button>
              </div>
            </div>
            <div>
              <label style={LBL}>النوع</label>
              <select value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} style={INP}>
                <option value="manual">يدوي</option><option value="gift">هدية</option><option value="paid_monthly">مدفوع شهري</option><option value="paid_yearly">مدفوع سنوي</option>
              </select>
              <label style={{ ...LBL, marginTop: 6 }}>المبلغ (اختياري)</label>
              <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} style={INP} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={LBL}>ملاحظة</label>
              <input value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} style={INP} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={save} style={BTN_P}>✅ حفظ</button>
            <button onClick={() => setShowForm(false)} style={BTN_G}>إلغاء</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
        {rows.length === 0 && <div style={{ color: '#64748b', textAlign: 'center', padding: 30, border: '1px dashed #334155', borderRadius: 12 }}>مفيش اشتراكات يدوية لسه</div>}
        {rows.map(r => {
          const active = new Date(r.ends_at) > new Date();
          return (
            <div key={r.id} style={{ background: '#1e293b', borderRadius: 10, padding: '10px 12px', border: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? '#4ade80' : '#f87171' }} />
              <span style={{ color: 'white', fontWeight: 'bold', fontSize: 13 }}>{r.customers?.name || r.customer_id}</span>
              <span style={{ color: '#94a3b8', fontSize: 11 }}>@{r.customers?.username}</span>
              <span style={{ color: '#a5b4fc', fontSize: 11 }}>{r.source}{r.amount ? ` — ${r.amount} ج` : ''}</span>
              <span style={{ color: '#94a3b8', fontSize: 11, marginInlineStart: 'auto' }}>حتى {fmtD(r.ends_at)}</span>
              <button onClick={() => del(r.id)} style={BTN_D}>🗑</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── تاب 5: روابط تليجرام ─────────────────────────────────────
function TelegramTab({ notify }) {
  const [rows, setRows] = useState([]);
  const load = async () => { const r = await botApi('links'); setRows(r.rows || []); };
  useEffect(() => { load(); }, []);
  const unlink = async (tid) => {
    if (!window.confirm('فك ربط حساب تليجرام ده؟ العميل هيحتاج يربط تاني.')) return;
    const r = await botApi('link-delete', { telegram_id: tid });
    if (r.error) notify(r.error, 'error'); else load();
  };
  return (
    <div>
      <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 0 }}>العملاء اللي ربطوا حساب تليجرام بحسابهم على الموقع. الربط بيتم من لينك البوت — من غير ما العميل يكتب باسورد في تليجرام.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 480, overflowY: 'auto' }}>
        {rows.length === 0 && <div style={{ color: '#64748b', textAlign: 'center', padding: 30, border: '1px dashed #334155', borderRadius: 12 }}>محدش ربط تليجرام لسه</div>}
        {rows.map(r => (
          <div key={r.telegram_id} style={{ background: '#1e293b', borderRadius: 10, padding: '10px 12px', border: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>📱</span>
            <span style={{ color: 'white', fontWeight: 'bold', fontSize: 13 }}>{r.customers?.name}</span>
            <span style={{ color: '#94a3b8', fontSize: 11 }}>@{r.customers?.username}</span>
            <span style={{ color: '#60a5fa', fontSize: 11 }}>↔ {r.telegram_username ? `@${r.telegram_username}` : r.telegram_name || r.telegram_id}</span>
            <span style={{ color: '#64748b', fontSize: 11, marginInlineStart: 'auto' }}>{fmt(r.linked_at)}</span>
            <button onClick={() => unlink(r.telegram_id)} style={BTN_D}>فك الربط</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── الإحصاءات (فوق التابات) — كل مربع بيفتح التاب/الفلتر المناسب ──
function Stats({ onPick }) {
  const [s, setS] = useState(null);
  useEffect(() => { botApi('stats').then(r => setS(r.error ? null : r)); }, []);
  if (!s) return null;
  const b = s.week_breakdown || {};
  const Box = ({ n, l, go }) => (
    <button onClick={go} title="اضغط للعرض"
      style={{ background: '#1e293b', borderRadius: 10, padding: '8px 12px', border: '1px solid #334155', minWidth: 90, textAlign: 'center', cursor: 'pointer', color: 'inherit', transition: 'border-color .15s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#667eea'} onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}>
      <div style={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>{n}</div><div style={{ color: '#94a3b8', fontSize: 10 }}>{l}</div>
    </button>
  );
  const locked = (b.login_required || 0) + (b.enroll_required || 0) + (b.expired || 0);
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
      <Box n={s.today} l="رسائل النهارده" go={() => onPick('conversations', { today: true })} />
      <Box n={s.week} l="آخر 7 أيام" go={() => onPick('conversations', { week: true })} />
      <Box n={s.total} l="الإجمالي" go={() => onPick('conversations', {})} />
      <Box n={b.ch_web || 0} l="ويب (أسبوع)" go={() => onPick('conversations', { channel: 'web', week: true })} />
      <Box n={b.ch_telegram || 0} l="تليجرام (أسبوع)" go={() => onPick('conversations', { channel: 'telegram', week: true })} />
      <Box n={b.open || 0} l="مشتركين (أسبوع)" go={() => onPick('conversations', { state: 'open', week: true })} />
      <Box n={locked} l="مقفولين (أسبوع)" go={() => onPick('conversations', { locked: true, week: true })} />
      <Box n={s.telegram_links} l="روابط تليجرام" go={() => onPick('telegram', {})} />
    </div>
  );
}

// ── المكوّن الرئيسي ─────────────────────────────────────────
export default function BotManager({ lang, isRTL, onClose, customers = [], perms = {} }) {
  const [tab, setTab] = useState('settings');
  const [convFilter, setConvFilter] = useState({});
  // ⏱️ تحديث تلقائي كل 60 ثانية (بيعيد تحميل الإحصاءات والتاب المفتوح) — مع إمكانية الإيقاف
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [tick, setTick] = useState(60);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => setTick(t => {
      if (t <= 1) { setRefreshKey(k => k + 1); return 60; }
      return t - 1;
    }), 1000);
    return () => clearInterval(id);
  }, [autoRefresh]);
  const refreshNow = () => { setRefreshKey(k => k + 1); setTick(60); };
  const pick = (t, filter) => { setConvFilter(filter || {}); setTab(t); };
  const [toast, setToast] = useState(null);
  const notify = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const TABS = [
    ['settings', '🧠 الشخصية والقواعد', 'can_manage_settings'],
    ['knowledge', '💡 قاعدة المعرفة', 'can_manage_settings'],
    ['conversations', '💬 المحادثات', 'can_view_customers'],
    ['subs', '🎟️ اشتراكات المساعد', 'can_view_customers'],
    ['telegram', '📱 تليجرام', 'can_view_customers'],
  ].filter(([, , p]) => !p || perms[p]);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '860px', background: '#0f172a', borderRadius: '20px', padding: '24px', boxShadow: '0 25px 60px rgba(0,0,0,0.5)', marginTop: '20px', direction: 'rtl' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <h2 style={{ color: 'white', fontWeight: 'bold', fontSize: 18, margin: 0 }}>🤖 مدير المساعد الذكي</h2>
            <p style={{ color: '#94a3b8', fontSize: 12, margin: '4px 0 0' }}>عقل واحد بيخدم الموقع وبوت تليجرام — أي تعديل هنا بيظهر في الاتنين فورًا</p>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={() => setAutoRefresh(v => !v)} title={autoRefresh ? 'إيقاف التحديث التلقائي' : 'تشغيل التحديث التلقائي'}
              style={{ padding: '6px 10px', background: autoRefresh ? '#166534' : '#1e293b', color: autoRefresh ? '#4ade80' : '#94a3b8', border: '1px solid #334155', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 'bold', minWidth: 96 }}>
              {autoRefresh ? `⏱️ تحديث بعد ${tick}s` : '⏸️ التحديث متوقف'}
            </button>
            <button onClick={refreshNow} title="تحديث الآن" style={{ padding: '6px 10px', background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>🔄</button>
            <button onClick={onClose} style={{ padding: '8px 12px', background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        <Stats key={`stats-${refreshKey}`} onPick={pick} />

        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', borderBottom: '1px solid #1e293b', paddingBottom: 10 }}>
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{ padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 'bold', background: tab === k ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#1e293b', color: tab === k ? 'white' : '#94a3b8' }}>{l}</button>
          ))}
        </div>

        {toast && <div style={{ background: toast.type === 'error' ? '#7f1d1d' : '#166534', color: 'white', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{toast.msg}</div>}

        {tab === 'settings' && <SettingsTab notify={notify} />}
        {tab === 'knowledge' && <KnowledgeManager key={`k-${refreshKey}`} lang={lang} isRTL={isRTL} embedded />}
        {tab === 'conversations' && <ConversationsTab key={`c-${refreshKey}`} initial={convFilter} />}
        {tab === 'subs' && <SubscriptionsTab key={`s-${refreshKey}`} notify={notify} customers={customers} />}
        {tab === 'telegram' && <TelegramTab key={`t-${refreshKey}`} notify={notify} />}
      </div>
    </div>
  );
}
